import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GridFourIcon } from "@phosphor-icons/react";

// Fleet Heatmap - Symbol x Tier grid on the Overview page.
//
// Each row = a traded symbol, each column = a tier. Cell colour intensity
// scales with the count so you can immediately spot patterns like "EURUSD
// has 3 champions but USDJPY has zero". Click a symbol to jump into the
// Symbol Leaders detail view.
const TIERS = [
  { key: "champion", label: "Champ", color: [214, 255, 56] },  // #D6FF38
  { key: "elite",    label: "Elite", color: [96, 165, 250] },  // #60A5FA
  { key: "basic",    label: "Basic", color: [34, 197, 94] },   // #22C55E
  { key: "testing",  label: "Test",  color: [249, 115, 22] },  // #F97316
  { key: "graveyard",label: "Grave", color: [255, 77, 77] },   // #FF4D4D
];

export default function FleetHeatmap({ limit = 12 }) {
  const [symbols, setSymbols] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/symbols");
        if (!cancelled) {
          setSymbols(data.symbols || []);
          setUnassignedCount(data.unassigned_count || 0);
        }
      } catch {
        if (!cancelled) setSymbols([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sort by fleet size then champion count; keep top-N so the grid doesn't
  // become a giant scroll target for users with lots of symbols.
  const rows = useMemo(() => {
    const arr = [...symbols].sort((a, b) =>
      (b.champion || 0) - (a.champion || 0) ||
      (b.total_bots || 0) - (a.total_bots || 0),
    );
    return arr.slice(0, limit);
  }, [symbols, limit]);

  // Cap for intensity scaling - anything higher just shows full saturation.
  const maxByTier = useMemo(() => {
    const m = {};
    for (const t of TIERS) {
      m[t.key] = Math.max(1, ...rows.map((r) => r[t.key] || 0));
    }
    return m;
  }, [rows]);

  const bgFor = (tier, count) => {
    if (!count) return "rgba(148,163,184,0.05)"; // muted for zeros
    const intensity = 0.15 + 0.75 * Math.min(1, count / maxByTier[tier.key]);
    const [r, g, b] = tier.color;
    return `rgba(${r},${g},${b},${intensity.toFixed(2)})`;
  };

  return (
    <div
      data-testid="fleet-heatmap"
      className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GridFourIcon size={14} weight="duotone" className="text-[#D6FF38]" />
          <div className="text-[11px] mono uppercase tracking-widest text-[#94A3B8]">
            Fleet Heatmap
          </div>
        </div>
        <div className="text-[10px] mono text-[#64748B]">symbol × tier</div>
      </div>

      {loading ? (
        <div className="text-xs text-[#64748B] mono py-6 text-center">loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[#64748B] mono py-6 text-center">
          no symbol data yet
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs mono">
            <thead>
              <tr className="text-[9px] text-[#64748B] uppercase tracking-widest">
                <th className="text-left pb-2 pr-3 font-normal">Symbol</th>
                {TIERS.map((t) => (
                  <th
                    key={t.key}
                    className="text-center pb-2 px-1 font-normal"
                    style={{ color: `rgb(${t.color.join(",")})` }}
                  >
                    {t.label}
                  </th>
                ))}
                <th className="text-right pb-2 pl-3 font-normal">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.symbol} className="border-t border-[#2A2D3A]/40">
                  <td className="py-1.5 pr-3">
                    <Link
                      to="/symbols"
                      className="text-[#F1F5F9] hover:text-[#D6FF38] transition-colors"
                    >
                      {r.symbol}
                    </Link>
                    {r.seeded && (
                      <span
                        className="ml-2 text-[9px] uppercase tracking-widest px-1 py-0.5 rounded"
                        style={{ color: "#D6FF38", background: "rgba(214,255,56,0.10)", border: "1px solid rgba(214,255,56,0.30)" }}
                        title="Configured in Seed Symbol Editor — awaiting first backtest"
                      >
                        seeded
                      </span>
                    )}
                  </td>
                  {TIERS.map((t) => {
                    const count = r[t.key] || 0;
                    // Cell text stays white on filled cells (max contrast
                    // against tinted background) and dim slate on zeros.
                    return (
                      <td key={t.key} className="p-0.5">
                        <div
                          className="text-center py-1.5 rounded font-medium"
                          style={{
                            background: bgFor(t, count),
                            color: count ? "#FFFFFF" : "#475569",
                            textShadow: count ? "0 1px 2px rgba(0,0,0,0.6)" : "none",
                          }}
                        >
                          {count}
                        </div>
                      </td>
                    );
                  })}
                  <td className="py-1.5 pl-3 text-right text-[#94A3B8]">
                    {r.total_bots}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && unassignedCount > 0 && (
        <div
          data-testid="fleet-heatmap-unassigned"
          className="mt-3 flex items-center justify-between border border-amber-500/30 bg-amber-500/5 rounded px-3 py-2"
        >
          <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-amber-400">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            {unassignedCount} bots missing symbol
          </div>
          <Link
            to="/symbols?unassigned=1"
            className="text-[10px] mono text-amber-300 hover:text-amber-200"
          >
            investigate →
          </Link>
        </div>
      )}
    </div>
  );
}
